import {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import { buildItemsPreview } from './utils';

// Stable identity for a namespace that has no preview yet.
const EMPTY_PREVIEW = { items: [], itemsCount: 0 };

/**
 * The item preview behind a hero page, read in every namespace the viewer can
 * see. A signed-in viewer gets both, because their counts are what decides
 * whether the scope control renders at all - the entity having a record in a
 * namespace says nothing about that namespace holding any of its items.
 *
 * `loaded` reports that the counts are settled, not that they arrived: a failed
 * fetch is as final as an empty one, and leaves that namespace absent from
 * `previews`. Until then a caller should hold off on anything that quotes a
 * count, rather than quoting a stale aggregate and correcting itself.
 */
export function useNamespacePreviews({
  entityId, namespace, scoped, pageSize, fetchPreview,
}) {
  const [previews, setPreviews] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [scope, setScope] = useState(null);

  const namespaces = useMemo(
    () => (scoped ? ['private', 'public'] : [namespace]),
    [scoped, namespace],
  );

  // Resolves to the previews keyed by namespace once a fetch was attempted.
  const load = useCallback(() => {
    if (!pageSize) return Promise.resolve(null);
    return Promise.all(namespaces.map(
      (ns) => fetchPreview(ns, pageSize).then(buildItemsPreview).catch(() => null),
    )).then((results) => Object.fromEntries(
      namespaces
        .map((ns, i) => [ns, results[i]])
        .filter(([, preview]) => preview),
    ));
  }, [namespaces, pageSize, fetchPreview]);

  const apply = (next) => {
    if (Object.keys(next).length) setPreviews(next);
    setLoaded(true);
  };

  // A new entity invalidates the previews and the pick.
  useEffect(() => {
    setPreviews({});
    setLoaded(false);
    setScope(null);
  }, [entityId]);

  useEffect(() => {
    let cancelled = false;
    load().then((next) => {
      if (next && !cancelled) apply(next);
    });
    return () => { cancelled = true; };
  }, [load]);

  const privateCount = previews.private?.itemsCount || 0;
  const publicCount = previews.public?.itemsCount || 0;
  // Empty segments stay visible but disabled to keep counts visible.
  const showScope = scoped && (privateCount > 0 || publicCount > 0);
  // Open on the namespace the page was opened in, falling back to the other
  // only once the previews say this one has nothing. Counts are 0 until they
  // load, so the fallback must not fire on the empty state.
  const activeNamespace = scoped
    ? (scope || (publicCount > 0 && privateCount === 0 ? 'public' : 'private'))
    : namespace;

  return {
    previews,
    loaded,
    activeNamespace,
    setScope,
    showScope,
    scopeOptions: [
      {
        value: 'private',
        label: 'Private',
        count: privateCount,
        disabled: privateCount === 0,
        title: 'Items in your private namespace',
      },
      {
        value: 'public',
        label: 'Public',
        count: publicCount,
        disabled: publicCount === 0,
        title: 'Items in the public namespace',
      },
    ],
    recent: previews[activeNamespace] || EMPTY_PREVIEW,
    // Sharing or bookmarking a card moves that item across the namespace
    // boundary, which changes both counts and both grids.
    refresh: () => load().then((next) => { if (next) apply(next); }),
  };
}

export default useNamespacePreviews;
